import { GraphType } from '@/graphql/types';
import { mixed, type AnySchema } from 'yup';
import { ValueScaleEquation, type ValueScaleEquationType } from '../../../../../graph/auto-scale';
import { availableAxis, type AxisType } from '../../../../../graph/axis';
import { ScaleType, type DataTypeValue } from '../../../../../graph/enums';
import { isRecord } from '../../../../../utils/typeGuards';
import { assertNoUnknownKeys } from '../runtimeBindingSettingsYupUtils';
import {
    DATA_TYPE_VALUES,
    GRAPH_SETTINGS_ALLOWED_KEYS,
    LEGEND_PLACEMENTS,
    SUPPORTED_GRAPH_TYPES,
    dataTypeSupportsGraphType,
    type GraphSettings,
    type SupportedGraphType,
} from './contracts';

function parseChoice<TChoice extends string>(
    value: unknown,
    choices: readonly TChoice[],
    path: string
): TChoice {
    if (typeof value !== 'string' || !choices.includes(value as TChoice)) {
        throw new Error(`Expected "${path}" to be one of: ${choices.join(', ')}.`);
    }

    return value as TChoice;
}

function parseOptionalChoice<TChoice extends string>(
    value: unknown,
    choices: readonly TChoice[],
    path: string
): TChoice | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    return parseChoice(value, choices, path);
}

function parseRequiredPositiveNumber(value: unknown, path: string): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        throw new Error(`Expected "${path}" to be a positive number.`);
    }

    return value;
}

function parseOptionalPositiveNumberOrNull(
    value: unknown,
    path: string
): number | null | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    if (value === null) {
        return null;
    }

    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        throw new Error(`Expected "${path}" to be a positive number or null.`);
    }

    return value;
}

function parseOptionalStringArray(value: unknown, path: string): string[] | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    if (!Array.isArray(value)) {
        throw new Error(`Expected "${path}" to be an array.`);
    }

    return value.map((entry, index) => {
        if (typeof entry !== 'string') {
            throw new Error(`Expected "${path}[${index}]" to be a string.`);
        }

        const trimmed = entry.trim();
        if (!trimmed.length) {
            throw new Error(`Expected "${path}[${index}]" to be a non-empty string.`);
        }

        return trimmed;
    });
}

function parseOptionalEnabledAxes(value: unknown, path: string): AxisType[] | undefined {
    if (typeof value === 'undefined') {
        return undefined;
    }

    if (!Array.isArray(value)) {
        throw new Error(`Expected "${path}" to be an array.`);
    }

    const seenAxes = new Set<AxisType>();

    return value.map((entry, index) => {
        const axis = parseChoice(entry, availableAxis, `${path}[${index}]`);
        if (seenAxes.has(axis)) {
            throw new Error(`Duplicate "${path}[${index}]" value "${axis}".`);
        }
        seenAxes.add(axis);

        return axis;
    });
}

function validateGraphTypeDataType(
    graphType: SupportedGraphType,
    dataType: DataTypeValue | undefined
): void {
    if (typeof dataType !== 'undefined' && !dataTypeSupportsGraphType(dataType, graphType)) {
        throw new Error(`Data type "${dataType}" is not supported for ${graphType} graphs.`);
    }
}

function validateFixedValueScale(
    valueScaleEquation: ValueScaleEquationType | undefined,
    valueScale: number | null | undefined
): void {
    if (valueScaleEquation === ValueScaleEquation.FIXED && typeof valueScale !== 'number') {
        throw new Error(
            'Expected "graph.valueScale" to be set when "graph.valueScaleEquation" is "fixed".'
        );
    }
}

export function normalizeGraphSettings(rawSettings: unknown): GraphSettings {
    if (!isRecord(rawSettings)) {
        throw new Error('Expected "graph" settings to be an object.');
    }

    assertNoUnknownKeys(rawSettings, GRAPH_SETTINGS_ALLOWED_KEYS, 'graph');

    const graphType = parseChoice(rawSettings.graphType, SUPPORTED_GRAPH_TYPES, 'graph.graphType');
    const legendPlacement = parseOptionalChoice(
        rawSettings.legendPlacement,
        LEGEND_PLACEMENTS,
        'graph.legendPlacement'
    );
    const preferredGraphType = parseOptionalChoice(
        rawSettings.preferredGraphType,
        Object.values(GraphType),
        'graph.preferredGraphType'
    );
    const frequencyGraphScaleType = parseOptionalChoice(
        rawSettings.frequencyGraphScaleType,
        Object.values(ScaleType),
        'graph.frequencyGraphScaleType'
    );
    const dataType = parseOptionalChoice(rawSettings.dataType, DATA_TYPE_VALUES, 'graph.dataType');
    const valueScale = parseOptionalPositiveNumberOrNull(
        rawSettings.valueScale,
        'graph.valueScale'
    );
    const valueScaleEquation = parseOptionalChoice(
        rawSettings.valueScaleEquation,
        Object.values(ValueScaleEquation),
        'graph.valueScaleEquation'
    );
    const frequencyScale = parseOptionalPositiveNumberOrNull(
        rawSettings.frequencyScale,
        'graph.frequencyScale'
    );
    const hiddenLines = parseOptionalStringArray(rawSettings.hiddenLines, 'graph.hiddenLines');
    const enabledAxes = parseOptionalEnabledAxes(rawSettings.enabledAxes, 'graph.enabledAxes');
    validateGraphTypeDataType(graphType, dataType);
    validateFixedValueScale(valueScaleEquation, valueScale);

    return {
        graphType,
        width: parseRequiredPositiveNumber(rawSettings.width, 'graph.width'),
        height: parseRequiredPositiveNumber(rawSettings.height, 'graph.height'),
        ...(typeof legendPlacement === 'undefined' ? {} : { legendPlacement }),
        ...(typeof preferredGraphType === 'undefined' ? {} : { preferredGraphType }),
        ...(typeof frequencyGraphScaleType === 'undefined' ? {} : { frequencyGraphScaleType }),
        ...(typeof dataType === 'undefined' ? {} : { dataType }),
        ...(typeof valueScale === 'undefined' ? {} : { valueScale }),
        ...(typeof valueScaleEquation === 'undefined' ? {} : { valueScaleEquation }),
        ...(typeof frequencyScale === 'undefined' ? {} : { frequencyScale }),
        ...(typeof hiddenLines === 'undefined' ? {} : { hiddenLines }),
        ...(typeof enabledAxes === 'undefined' ? {} : { enabledAxes }),
    };
}

export function buildGraphSettingsYupSchema(): AnySchema {
    return mixed()
        .required('Expected "graph" settings to be an object.')
        .test('graph-settings-shape', 'Invalid graph settings.', function test(value) {
            if (!isRecord(value)) {
                return this.createError({
                    message: 'Expected "graph" settings to be an object.',
                });
            }

            try {
                normalizeGraphSettings(value);
                return true;
            } catch (error) {
                const normalizedError = error instanceof Error ? error : new Error(String(error));
                return this.createError({ message: normalizedError.message });
            }
        });
}
